import { Component, Injector, OnInit } from '@angular/core';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'src-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class OrganizationDashboardComponent extends BaseComponent implements OnInit {
  constructor(private encounterService: EncounterService, private injector: Injector) {
    super(injector);
  }

  private organizationId: string = '';
  public totalFinishedEncounters: number = 0;
  public totalExaminationEncounters: number = 0;
  public totalConsultationEncounters: number = 0;
  public totalAmountInvoiced: number = 0;
  public totalExaminationAmountInvoiced: number = 0;
  public totalConsultationAmountInvoiced: number = 0;
  public plannedEncounters: number = 0;
  public totalPlannedEncountersByType: { Consulta: number; Exame: number } = { Consulta: 0, Exame: 0 };
  public totalCanceledEncounters: number = 0;
  public encountersByDoctor: { [key: string]: number } = {};
  public encountersBySecretary: { [key: string]: number } = {};
  public encountersByHealthPlan: { [key: string]: number } = {};
  public doctorName: string = '';
  public secretaryName: string = '';
  public planName: string = '';
  public totalEncountersByType: { Consulta: number; Exame: number } = { Consulta: 0, Exame: 0 };
  public selectedAppointmentType: 'Consulta' | 'Exame' = 'Consulta';
  public selectedPlannedAppointmentType: 'Consulta' | 'Exame' = 'Consulta';
  public selectedPeriod: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom' = 'daily';
  public totalEncounters: number = 0;
  public startDate?: Date;
  public endDate?: Date;

  ngOnInit() {
    if (!this.isLoggedIn) {
      this.router.navigate(['login']);
    }
    this.currentUser = this.auth.currentUserValue;
    this.organizationId = this.currentUser.user.organization_id;

    this.getEncounters(this.organizationId);
  }

  public get isLoggedIn(): boolean {
    return this.auth.isAuthenticated();
  }

  getEncounters(organization_id: string) {
    this.encounterService.listByOrganizationNew(organization_id).subscribe({
      next: (resEncounter: any) => {
        this.calculateStatistics(resEncounter);
        this.countEncountersByType(resEncounter);

        const plannedTrendData = Array(12).fill(0);
        const finishedTrendData = Array(12).fill(0);
        const canceledTrendData = Array(12).fill(0);

        resEncounter.forEach((encounter: any) => {
          const month = new Date(encounter.startDate).getMonth();
          if (encounter.status === 'planned') {
            plannedTrendData[month]++;
          } else if (encounter.status === 'finished') {
            finishedTrendData[month]++;
          } else if (encounter.status === 'cancelled') {
            canceledTrendData[month]++;
          }
        });

        this.renderAppointmentTrendChart(finishedTrendData, plannedTrendData, canceledTrendData);
      },
      error: (err) => {
        console.error('Failed to search encounters', err);
      },
    });
  }

  calculateStatistics(encounters: any) {
    this.totalFinishedEncounters = encounters.filter((encounter: any) => encounter.status === 'finished').length;
    this.totalExaminationEncounters = encounters.filter((encounter: any) => encounter.type === 'Exame' && encounter.status === 'finished').length;
    this.totalConsultationEncounters = encounters.filter((encounter: any) => encounter.type === 'Consulta' && encounter.status === 'finished').length;
    this.totalCanceledEncounters = encounters.filter((encounter: any) => encounter.status === 'cancelled').length;
    this.plannedEncounters = encounters.filter((encounter: any) => encounter.status === 'planned').length;

    this.totalAmountInvoiced = encounters
      .filter((encounter: any) => encounter.status === 'finished')
      .reduce((acc: number, encounter: any) => acc + parseFloat(encounter.value || '0'), 0);

    this.totalExaminationAmountInvoiced = encounters
      .filter((encounter: any) => encounter.status === 'finished' && encounter.type === 'Exame')
      .reduce((acc: number, encounter: any) => acc + parseFloat(encounter.value || '0'), 0);

    this.totalConsultationAmountInvoiced = encounters
      .filter((encounter: any) => encounter.status === 'finished' && encounter.type === 'Consulta')
      .reduce((acc: number, encounter: any) => acc + parseFloat(encounter.value || '0'), 0);

    this.encountersByDoctor = encounters.reduce((acc: any, encounter: any) => {
      const doctorName = encounter.practitioner.name;
      acc[doctorName] = (acc[doctorName] || 0) + (encounter.status === 'finished' ? 1 : 0);
      return acc;
    }, {});

    this.encountersBySecretary = encounters.reduce((acc: any, encounter: any) => {
      const secretaryName = encounter.secretary.name;
      acc[secretaryName] = (acc[secretaryName] || 0) + (encounter.status === 'finished' ? 1 : 0);
      return acc;
    }, {});

    this.encountersByHealthPlan = encounters.reduce((acc: any, encounter: any) => {
      const healthPlanName = encounter.health_plan?.health_plan_name;

      if (healthPlanName) {
        acc[healthPlanName] = (acc[healthPlanName] || 0) + (encounter.status === 'finished' ? 1 : 0);
      }

      return acc;
    }, {});
  }

  countEncountersByType(encounters: any) {
    this.totalPlannedEncountersByType = encounters.reduce(
      (acc: any, encounter: any) => {
        if (encounter.status === 'planned') {
          if (encounter.type === 'Consulta') {
            acc.Consulta = (acc.Consulta || 0) + 1;
          } else if (encounter.type === 'Exame') {
            acc.Exame = (acc.Exame || 0) + 1;
          }
        }
        return acc;
      },
      { Consulta: 0, Exame: 0 }
    );

    this.totalEncountersByType = encounters.reduce(
      (acc: any, encounter: any) => {
        if (encounter.status === 'finished') {
          if (encounter.type === 'Consulta') {
            acc.Consulta = (acc.Consulta || 0) + 1;
          } else if (encounter.type === 'Exame') {
            acc.Exame = (acc.Exame || 0) + 1;
          }
        }
        return acc;
      },
      { Consulta: 0, Exame: 0 }
    );
  }

  updateAppointmentType() {
    // this.selectedAppointmentType
  }

  onPeriodChange() {
    if (this.selectedPeriod !== 'custom') {
      this.getEncountersByPeriod(this.selectedPeriod);
    }
  }

  onCustomDateChange() {
    if (this.startDate && this.endDate) {
      this.getEncountersByCustomDateRange(this.startDate, this.endDate);
    }
  }

  getEncountersByCustomDateRange(startDate: Date, endDate: Date) {
    this.encounterService.listByOrganizationNew(this.organizationId).subscribe({
      next: (encounters: any) => {
        this.totalEncounters = encounters.filter(
          (encounter: any) =>
            encounter.status === 'finished' && new Date(encounter.startDate) >= startDate && new Date(encounter.startDate) <= endDate
        ).length;
      },
      error: (err) => {
        console.error('Failed to search encounters', err);
      },
    });
  }

  getEncountersByPeriod(period: string) {
    const currentDate = new Date();
    const startDate = new Date();
    let endDate = new Date();

    switch (period) {
      case 'daily':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'weekly':
        const weekStart = currentDate.getDate() - currentDate.getDay();
        startDate.setDate(weekStart);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(weekStart + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'monthly':
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yearly':
        startDate.setMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setMonth(0, 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        break;
    }

    this.encounterService.listByOrganizationNew(this.organizationId).subscribe({
      next: (encounters: any) => {
        this.totalEncounters = encounters.filter(
          (encounter: any) =>
            encounter.status === 'finished' && new Date(encounter.startDate) >= startDate && new Date(encounter.startDate) <= endDate
        ).length;
      },
      error: (err) => {
        console.log('Failed to search encounters', err);
      },
    });
  }

  renderAppointmentTrendChart(finishedTrendData: number[], plannedTrendData: number[], canceledTrendData: number[]) {
    const ctx = document.getElementById('appointmentTrendChart') as HTMLCanvasElement;

    new Chart(ctx, {
      type: 'line',
      data: {
        labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        datasets: [
          {
            label: 'Realizados',
            data: finishedTrendData,
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.4,
          },
          {
            label: 'Planejados',
            data: plannedTrendData,
            borderColor: 'rgba(255, 159, 64, 1)',
            backgroundColor: 'rgba(255, 159, 64, 0.2)',
            tension: 0.4,
          },
          {
            label: 'Cancelados',
            data: canceledTrendData,
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            tension: 0.4,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Meses',
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Quantidade',
            },
          },
        },
      },
    });
  }
}
